const SHELL_FETCH_TIMEOUT_MS = 5000

const APP_SHELL_CACHE = 'app-shell'
const SHELL_DOCUMENT = '/index.html'

// Caches holding the app's CODE that are safe to purge on every recovery.
// js-cache/css-cache are runtime *fallback* caches — recovery only runs online
// (both callers gate on a reachable server), so the network and the Workbox
// precache repopulate them. Deleting them here drops any stale chunk a partial
// previous load may have cached.
const ALWAYS_PURGE_CACHE_NAMES = ['js-cache', 'css-cache']

// Caches that hold (or back) the app SHELL. Deleting these strands the client
// with no document to render, so they are ONLY purged when we already hold a
// fresh shell in memory to put back — keeping the final cache state monotonic
// (see recoverToLatestBuild). `workbox-precache*` is matched by prefix (Workbox
// suffixes it with a version + origin). Content caches — `media-uploads-cache`
// (prefetched attachments), `image-cache`, `font-cache` — are deliberately
// absent: field users prefetch photos and a version bump must not wipe them.
const SHELL_BEARING_CACHE_NAMES = [APP_SHELL_CACHE]
const SHELL_BEARING_CACHE_PREFIXES = ['workbox-precache']

interface RecoverToLatestBuildOptions {
  reloadParam: string
  logPrefix: string
}

export function buildReloadUrl(
  currentUrl: string,
  reloadParam: string,
  markerValue: number | string,
) {
  const nextUrl = new URL(currentUrl)
  nextUrl.searchParams.set(reloadParam, String(markerValue))
  return nextUrl.toString()
}

/**
 * Decide which Cache Storage entries a recovery should delete, given the full
 * list of cache names. Pure (no `caches` access) so the allowlist is unit
 * testable, mirroring the codebase's `*Decisions.ts` helpers.
 *
 * - app-code fallback caches (js-cache, css-cache) → always purged.
 * - shell-bearing caches (app-shell, workbox-precache*) → purged ONLY when
 *   `purgeShellCaches` is true (i.e. a fresh shell is in hand to repopulate).
 * - everything else (media/image/font, plus any cache we don't recognise) → kept.
 */
export function selectCachesToPurge(
  cacheNames: string[],
  { purgeShellCaches }: { purgeShellCaches: boolean },
): string[] {
  return cacheNames.filter(name => {
    if (ALWAYS_PURGE_CACHE_NAMES.includes(name)) return true
    if (!purgeShellCaches) return false
    if (SHELL_BEARING_CACHE_NAMES.includes(name)) return true
    return SHELL_BEARING_CACHE_PREFIXES.some(prefix => name.startsWith(prefix))
  })
}

/**
 * Fetch the latest app shell, bypassing the HTTP cache, and HOLD it in memory.
 * The returned Response body is left unread so the caller can `put()` it into
 * the app-shell cache. Timeout-bounded so a hung/half-open network can't
 * dead-lock the whole recovery sequence. Returns null on any failure — the
 * caller then preserves the existing shell rather than stranding the client.
 */
async function fetchFreshShell(logPrefix: string): Promise<Response | null> {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), SHELL_FETCH_TIMEOUT_MS)
  try {
    const response = await fetch(SHELL_DOCUMENT, {
      credentials: 'same-origin',
      cache: 'reload',
      signal: controller.signal,
    })
    if (response.ok) return response
    console.warn(
      `[${logPrefix}] Fresh shell fetch returned ${response.status}; preserving existing shell`,
    )
    return null
  } catch (error) {
    console.warn(`[${logPrefix}] Fresh shell fetch failed; preserving existing shell`, error)
    return null
  } finally {
    window.clearTimeout(timeoutId)
  }
}

/**
 * Delete app-CODE caches by allowlist. Content caches (media/image/font) and
 * any unrecognised cache are preserved. Shell-bearing caches are purged only
 * when `purgeShellCaches` is true — the caller passes true only when it holds a
 * fresh shell to repopulate, so the client is never left without a document.
 */
async function clearAppCodeCaches(logPrefix: string, purgeShellCaches: boolean) {
  if (!('caches' in window)) return

  try {
    const keys = await caches.keys()
    const toDelete = selectCachesToPurge(keys, { purgeShellCaches })
    await Promise.all(toDelete.map(key => caches.delete(key)))
    console.log(`[${logPrefix}] Cleared app-code caches: ${toDelete.join(', ') || '(none)'}`)
  } catch (error) {
    console.error(`[${logPrefix}] Failed to clear app-code caches:`, error)
  }
}

/**
 * Put the freshly-fetched shell back into the app-shell runtime cache. The SW's
 * navigation route checks this cache before the Workbox precache, so the next
 * navigation (and any offline reload) renders this known-good document even if
 * the precache was purged or the SW update did not land.
 */
async function repopulateAppShell(logPrefix: string, freshShell: Response) {
  if (!('caches' in window)) return

  try {
    const cache = await caches.open(APP_SHELL_CACHE)
    await cache.put(SHELL_DOCUMENT, freshShell)
    console.log(`[${logPrefix}] Repopulated ${APP_SHELL_CACHE} with fresh ${SHELL_DOCUMENT}`)
  } catch (error) {
    console.error(`[${logPrefix}] Failed to repopulate app-shell:`, error)
  }
}

// Kick the service worker to fetch + install the latest worker. FIRE-AND-FORGET
// by design — we deliberately do NOT await it. On Safari/WebKit
// registration.update() can hang for seconds; awaiting it (even with a timeout)
// made the toast's "Reload" button feel dead — the page only refreshed once the
// await gave up. There is nothing to wait for: the forced reload below
// revalidates the worker anyway (registration uses updateViaCache: 'none'), and
// the network-first navigation route serves the fresh shell regardless of which
// worker is currently active. The new worker then activates on the next load.
function kickServiceWorkerUpdate(logPrefix: string) {
  if (!('serviceWorker' in navigator)) return

  void navigator.serviceWorker
    .getRegistration()
    .then(registration => registration?.update())
    .then(
      () => console.log(`[${logPrefix}] SW update kicked`),
      error => console.error(`[${logPrefix}] SW update kick failed:`, error),
    )
}

/**
 * Recover a client to the latest build without losing offline-first data.
 *
 * The sequence is MONOTONIC — the final cache state always contains a
 * known-good shell:
 *   1. Fetch the latest /index.html and HOLD it in memory (do NOT cache it yet;
 *      step 3 may delete the app-shell cache).
 *   2. Kick the SW update in the BACKGROUND (fire-and-forget) — never block the
 *      reload on it (Safari's registration.update() can hang for seconds).
 *   3. Purge stale app-code caches by allowlist — preserving media/image/font.
 *      The shell-bearing caches (app-shell, workbox-precache*) are purged ONLY
 *      when step 1 produced a fresh shell to put back.
 *   4. Recreate the app-shell cache with that fresh shell.
 *   5. Reload to the latest build.
 *
 * Without the hold-in-memory → delete → recreate ordering, step 3 would delete
 * the very shell step 1 just fetched.
 */
export async function recoverToLatestBuild({
  reloadParam,
  logPrefix,
}: RecoverToLatestBuildOptions) {
  console.log(`[${logPrefix}] Starting reload sequence`)

  // 1. Grab the latest shell and hold it in memory before touching any cache.
  const freshShell = await fetchFreshShell(logPrefix)

  // 2. Kick the SW update in the background — do NOT block the reload on it.
  kickServiceWorkerUpdate(logPrefix)

  // 3. Purge stale app-code caches. Only purge the shell-bearing caches when we
  //    actually hold a fresh shell to put back — otherwise we'd strand the
  //    client with no document (non-monotonic).
  await clearAppCodeCaches(logPrefix, freshShell !== null)

  // 4. Recreate the app-shell with the fresh shell so the post-reload
  //    navigation — and any offline reload — always finds a known-good document.
  if (freshShell) {
    await repopulateAppShell(logPrefix, freshShell)
  }

  // 5. Reload to the latest build.
  const reloadUrl = buildReloadUrl(window.location.href, reloadParam, Date.now())
  console.log(`[${logPrefix}] Reloading to:`, reloadUrl)
  window.location.replace(reloadUrl)
}
